import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, inr } from "../lib/api";
import Shell, { PageHeader } from "../components/Shell";
import { CalendarBlank, FilePdf, CheckCircle, Wallet } from "@phosphor-icons/react";

export default function DayClose() {
  const { storeId } = useParams();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [preview, setPreview] = useState(null);
  const [counted, setCounted] = useState({ cash: "", upi: "", card: "", other: "" });
  const [openingCash, setOpeningCash] = useState("");
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadPreview = useCallback(async () => {
    try {
      const r = await api.get(`/store/${storeId}/day-close/preview?date=${date}`);
      setPreview(r.data);
      setCounted({
        cash: r.data.expected.cash || "",
        upi: r.data.expected.upi || "",
        card: r.data.expected.card || "",
        other: r.data.expected.other || "",
      });
    } catch (e) { setPreview(null); }
  }, [storeId, date]);

  const loadHistory = useCallback(async () => {
    const r = await api.get(`/store/${storeId}/day-closes`);
    setHistory(r.data);
  }, [storeId]);

  useEffect(() => { loadPreview(); loadHistory(); }, [loadPreview, loadHistory]);

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        store_id: storeId, date,
        opening_cash: parseFloat(openingCash) || 0,
        cash_counted: parseFloat(counted.cash) || 0,
        upi_counted: parseFloat(counted.upi) || 0,
        card_counted: parseFloat(counted.card) || 0,
        other_counted: parseFloat(counted.other) || 0,
        notes,
      };
      const { data } = await api.post("/store/day-close", body);
      const token = localStorage.getItem("pos_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store/day-close/${data.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
      loadHistory();
    } catch (e) { alert("Failed: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const openPdf = async (id) => {
    const token = localStorage.getItem("pos_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store/day-close/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const variance = (m) => (parseFloat(counted[m]) || 0) - (preview?.expected[m] || 0);
  const totalCounted = ["cash", "upi", "card", "other"].reduce((a, m) => a + (parseFloat(counted[m]) || 0), 0);
  const totalExpected = preview?.total_expected || 0;
  const totalVar = totalCounted - totalExpected;

  return (
    <Shell>
      <PageHeader title="Day-End Close · Z-Report" subtitle="Reconcile counted cash & digital payments against expected totals. Generate a Z-Report PDF." testId="dayclose-header" />
      <div className="p-8 space-y-5">
        <div className="bg-white border border-slate-200 rounded-md p-5 flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Date</label>
            <div className="relative">
              <CalendarBlank size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="dc-date" className="h-10 pl-7 pr-3 border border-slate-300 rounded-md text-sm font-mono-num" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Opening Cash</label>
            <input type="number" step="0.01" value={openingCash} onChange={(e) => setOpeningCash(e.target.value)} placeholder="0" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num w-32" />
          </div>
          <button onClick={loadPreview} className="h-10 px-4 bg-slate-900 text-white text-sm font-semibold rounded-md ml-auto">Refresh</button>
        </div>

        {preview && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Bills</div>
                <div className="font-display font-bold text-2xl font-mono-num mt-1">{preview.bills_count}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{preview.cancelled_count} cancelled</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Gross Sales</div>
                <div className="font-display font-bold text-2xl font-mono-num mt-1">{inr(preview.gross_sales)}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Cancelled</div>
                <div className="font-display font-bold text-2xl font-mono-num mt-1 text-red-600">{inr(preview.returns_total)}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-4">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Net Sales</div>
                <div className="font-display font-bold text-2xl font-mono-num mt-1 text-emerald-700">{inr(preview.net_sales)}</div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-5">
              <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-3"><Wallet size={18} />Payment Reconciliation</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="text-left px-2 py-2 font-semibold">Method</th>
                    <th className="text-right px-2 py-2 font-semibold">Expected</th>
                    <th className="text-right px-2 py-2 font-semibold">Counted</th>
                    <th className="text-right px-2 py-2 font-semibold">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {["cash", "upi", "card", "other"].map((m) => {
                    const v = variance(m);
                    return (
                      <tr key={m} className="border-b border-slate-100">
                        <td className="px-2 py-2 uppercase font-semibold tracking-wider text-sm">{m}</td>
                        <td className="px-2 py-2 text-right font-mono-num">{inr(preview.expected[m] || 0)}</td>
                        <td className="px-2 py-2 text-right"><input type="number" step="0.01" value={counted[m]} onChange={(e) => setCounted({ ...counted, [m]: e.target.value })} data-testid={`count-${m}`} className="w-28 h-8 px-2 border border-slate-300 rounded-md text-xs font-mono-num text-right" /></td>
                        <td className={`px-2 py-2 text-right font-mono-num font-bold ${v === 0 ? "text-slate-400" : v < 0 ? "text-red-600" : "text-emerald-700"}`}>{v >= 0 ? "+" : ""}{v.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-slate-900 text-white">
                    <td className="px-2 py-2 font-bold uppercase tracking-widest">Total</td>
                    <td className="px-2 py-2 text-right font-mono-num font-bold">{inr(totalExpected)}</td>
                    <td className="px-2 py-2 text-right font-mono-num font-bold">{inr(totalCounted)}</td>
                    <td className={`px-2 py-2 text-right font-mono-num font-bold ${Math.abs(totalVar) < 0.005 ? "" : totalVar < 0 ? "text-red-300" : "text-emerald-300"}`}>{totalVar >= 0 ? "+" : ""}{totalVar.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
              {(preview.pending_total || 0) > 0 && (
                <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="pending-total-note">
                  ⏳ <b>{inr(preview.pending_total)}</b> in PENDING (unpaid) bills — not counted in expected totals. Collect later.
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-5">
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Notes (optional)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" placeholder="Cash shortage from petty expense, etc." />
            </div>

            <button onClick={submit} disabled={saving} data-testid="close-day-btn" className="h-12 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-50"><CheckCircle size={16} weight="bold" />{saving ? "Closing…" : "Close Day & Generate Z-Report"}</button>
          </>
        )}

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-5 py-3 border-b border-slate-200 font-display font-bold">Day-Close History</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-2 font-semibold">Date</th>
                <th className="text-left px-4 py-2 font-semibold">Closed by</th>
                <th className="text-right px-4 py-2 font-semibold">Bills</th>
                <th className="text-right px-4 py-2 font-semibold">Net Sales</th>
                <th className="text-right px-4 py-2 font-semibold">Variance</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-sm">No day-close records yet.</td></tr>}
              {history.map((z) => (
                <tr key={z.id} className="border-b border-slate-100">
                  <td className="px-4 py-2 font-mono-num font-semibold">{z.date}</td>
                  <td className="px-4 py-2 text-slate-600 text-xs">{z.closed_by_email}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{z.bills_count}</td>
                  <td className="px-4 py-2 text-right font-mono-num font-bold">{inr(z.net_sales)}</td>
                  <td className={`px-4 py-2 text-right font-mono-num font-bold ${Math.abs(z.total_variance) < 0.01 ? "text-slate-400" : z.total_variance < 0 ? "text-red-600" : "text-emerald-700"}`}>{z.total_variance >= 0 ? "+" : ""}{z.total_variance.toFixed(2)}</td>
                  <td className="px-4 py-2 text-right"><button onClick={() => openPdf(z.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900"><FilePdf size={13} />PDF</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
}
